import React from 'react';
import _ from 'lodash';

import {
  InventoryActivity_ItemTabDocument,
  InventoryActivity_ItemTabQuery,
  SaveListingManagementStrategiesByStoreInput_ItemInput,
  SaveListingManagementStrategiesByStoreMutation,
  useListItemModalQuery,
  useSaveListingManagementStrategiesByStoreMutation
} from 'generated/graphql';

import Big from 'big.js';
import { gqlCacheUtil } from 'modules';



export interface IItem {
  itemId: string;
  sku: string;
  name: string;
  defaultPriceDollars: Big;
}

export interface IData {
  items: IItem[];
}

export function useQuery(storeId: string) {
  const tuple = useListItemModalQuery({
    variables: {
      storeId
    }
  });

  return React.useMemo(
    () => {
      const data: IData = {
        items: _.map(tuple.data?.items.nodes, (node): IItem => ({
          itemId: node.id,
          name: node.name,
          sku: node.sku,
          defaultPriceDollars: new Big(node.defaultPriceDollars)
        }))
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export interface IItemWithDefaultPrice {
  priceDollars: Big;
  itemId: string;
}

export function useSaveReportingStrategiesByStore(storeId: string) {
  const [fnMutate, tuple] = useSaveListingManagementStrategiesByStoreMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (items: IItemWithDefaultPrice[]) => {
      if (!_.isEmpty(items)) {
        await fnMutate({
          variables: {
            input: {
              storeId,
              items: _.map(items, (item): SaveListingManagementStrategiesByStoreInput_ItemInput => ({
                itemId: item.itemId,
                priceDollars: item.priceDollars.toFixed(2)
              }))
            }
          },
          updateQueries: gqlCacheUtil.genUpdateQueryMap<InventoryActivity_ItemTabQuery, SaveListingManagementStrategiesByStoreMutation>(
            InventoryActivity_ItemTabDocument,
            (prevState, { mutationResult }) => ({
              ...prevState,
              items: {
                ...prevState.items,
                nodes: mutationResult.data?.saveListingManagementStrategiesByStore ?? []
              }
            })
          )
        });
      }
    },
    [
      fnMutate,
      storeId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
